/**
 * Normalizes a Builder ID list field to an array of numeric IDs.
 * Handles the nested structure: [{ key: { value: '2101' } }] → [2101] or [{ key: '2101' }] → [2101]
 * @param {Array} builderIds - ID list from a Builder data field (categories, tags, etc.)
 * @param {string} key - The key to extract the ID from each item (e.g., 'category' or 'tagId')
 * @returns {Array<number>} - Array of numeric IDs
 */
const normalizeBuilderIds = (builderIds, key) => {
  if (!Array.isArray(builderIds)) {
    return [];
  }

  return builderIds
    .map((item) => {
      // Handle nested structure: { key: { value: 'ID' } }
      const value = item?.[key]?.value || item?.[key] || item;

      // Convert string to number if needed
      const numValue = typeof value === 'string' ? parseInt(value, 10) : value;

      // Only include valid numbers
      return typeof numValue === 'number' && !isNaN(numValue) ? numValue : null;
    })
    .filter((id) => id !== null);
};

/**
 * Calculates the intersection of two arrays
 * @param {Array} arr1 - First array
 * @param {Array} arr2 - Second array
 * @returns {Array} - Elements present in both arrays
 */
const intersection = (arr1, arr2) => {
  if (!Array.isArray(arr1) || !Array.isArray(arr2)) {
    return [];
  }
  return arr1.filter((item) => arr2.includes(item));
};

/**
 * Matches Builder size guide entries based on category overlap with product categories,
 * optionally boosted by default category and tag matching.
 *
 * Sort priority:
 *  1. Relevance score (defaultCategoryMatch + tagMatch) — descending
 *  2. Category overlap count — descending
 *  3. Most recently published — descending
 *  4. Fewer total categories (more specific guide) — ascending
 *  5. Entry ID — ascending (stable tiebreaker)
 *
 * @param {Array} sizeGuideEntries - Array of Builder size guide content entries
 * @param {Array<number>} productCategories - Product category IDs to match against
 * @param {Object} [options={}] - Optional matching dimensions
 * @param {number} [options.defaultCategoryId] - Product's default category ID for bonus scoring
 * @param {Array<number>} [options.tagIdList] - Product tag IDs; +1 if entry tags overlap, -1 if entry has tags but none match (penalizes specificity mismatch)
 * @returns {Object|null} - Best matching size guide entry, or null if no matches
 */
export const matchBuilderSizeGuide = (
  sizeGuideEntries,
  productCategories,
  { defaultCategoryId, tagIdList } = {}
) => {
  // Validate inputs
  if (!Array.isArray(sizeGuideEntries) || sizeGuideEntries.length === 0) {
    return null;
  }

  if (!Array.isArray(productCategories) || productCategories.length === 0) {
    return null;
  }

  // Map each entry to include match count, relevance score, and date
  const candidates = sizeGuideEntries
    .map((entry) => {
      const entryCategories = entry?.data?.categories;

      if (!Array.isArray(entryCategories)) {
        return null;
      }

      // Normalize Builder categories to numeric IDs
      const normalizedCategories = normalizeBuilderIds(
        entryCategories,
        'category'
      );

      if (normalizedCategories.length === 0) {
        return null;
      }

      const matchingCategories = intersection(
        normalizedCategories,
        productCategories
      );

      const matchCount = matchingCategories.length;

      // Extract publish date - prefer lastUpdated, fall back to firstPublished
      const dateString = entry.lastUpdated || entry.firstPublished;
      const publishDate = dateString ? new Date(dateString) : new Date(0);

      // Relevance score: signals that shift ranking up or down relative to pure category overlap
      const defaultCategoryMatch =
        defaultCategoryId != null &&
        normalizedCategories.includes(defaultCategoryId)
          ? 1
          : 0;

      // Tagged guides are specific: +1 if product shares a tag, -1 if not (penalizes mismatch
      // so a general guide without tags scores higher than a specific guide for the wrong product)
      const entryTags = normalizeBuilderIds(entry?.data?.tagIds, 'tagId');
      let tagMatch = 0;
      if (entryTags.length > 0) {
        tagMatch =
          Array.isArray(tagIdList) &&
          intersection(entryTags, tagIdList).length > 0
            ? 1
            : -1;
      }

      const relevanceScore = defaultCategoryMatch + tagMatch;

      return {
        entry,
        matchCount,
        publishDate,
        relevanceScore,
        totalCategories: normalizedCategories.length,
      };
    })
    .filter((candidate) => candidate !== null && candidate.matchCount > 0); // Only keep entries with at least 1 match

  // No matches found
  if (candidates.length === 0) {
    return null;
  }

  // Sort by: (1) relevance score desc, (2) most matches desc, (3) most recent date desc,
  //          (4) fewer total categories asc (more specific), (5) entry id asc
  candidates.sort((a, b) => {
    if (a.relevanceScore !== b.relevanceScore) {
      return b.relevanceScore - a.relevanceScore;
    }
    if (a.matchCount !== b.matchCount) {
      return b.matchCount - a.matchCount; // More matches first
    }
    if (a.publishDate - b.publishDate !== 0) {
      return b.publishDate - a.publishDate; // More recent first
    }
    if (a.totalCategories !== b.totalCategories) {
      return a.totalCategories - b.totalCategories; // Fewer categories (more specific) first
    }
    // Stable tiebreaker by entry id
    const aId = a.entry.id ?? '';
    const bId = b.entry.id ?? '';
    if (aId < bId) {
      return -1;
    }
    return aId > bId ? 1 : 0;
  });

  return candidates[0].entry;
};
